import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  useTheme,
  useMediaQuery
} from '@mui/material';
import ToggleCard from '../components/ToggleCard';

// Define the shape of a story
interface Story {
  id: number;
  title: string;       // Project name
  location: string;    // Project location
  date: string;        // Project date
  description: string;
  imgUrl: string;
}

const Engagement: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

  // Sample feed of impact stories
  const stories: Story[] = [
    {
      id: 1,
      title: 'Mangrove - Mangrove Planting Day',
      location: 'Aceh Timur, Indonesia',
      date: 'June 24, 2025',
      description:
        'From the selection of mangrove seedlings in nurseries to their transport via boats, community members—young and old—work together at the plantation site, planting the seedlings along the water’s edge to protect the coastline from erosion and restore critical habitats.',
      imgUrl:
        'https://handprint-live.s3.ap-southeast-1.amazonaws.com/uploads/raas/Partners/YAKOPI/Aceh+Timur/YAK-worker_boat_mangroves_Aceh-0624(2)+(1).avif'
    },
    {
      id: 2,
      title: 'Education - New Classroom in Colombia',
      location: 'Cauca, Colombia',
      date: 'March 15, 2025',
      description:
        'Local families collaborated to build a new classroom, ensuring children in Colombia have a safe, green space to learn and grow.',
      imgUrl:
        'https://handprint-live.s3.amazonaws.com/uploads/raas/Partners/ThinkEqual/Colombia/TEQ-CO-Cover2.avif'
    },
    {
      id: 3,
      title: 'Plastic - Beach Cleanup Initiative',
      location: 'Port Said, Egypt',
      date: 'March 5, 2025',
      description:
        'Volunteers gathered early in the morning to remove plastic waste from the shoreline, collecting over 200 kg of debris and preventing it from entering the ocean ecosystem.',
      imgUrl:
        'https://handprint-live.s3.amazonaws.com/uploads/raas/Partners/PlasticBank/Egypt/Stories/Plastic-Bank-Portsaid-Beach-Shoot-_45_.avif'
    },
    {
      id: 6,
      title: 'Mangrove - How we restore mangroves',
      location: 'Aceh Timur, Indonesia',
      date: 'Feb 6, 2025',
      description:
        'We grow seedlings in a nursery. Later on propagules are getting ready for planting. We build stakes for support. Propagules and stakes are loaded onto boats, carried to the plantation site in Aceh, Indonesia. The mangroves are planted in line, and we can see signs of biodiversity in the restoration area, with purple heron or good old crabs!',
      imgUrl:
        'https://handprint-live.s3.amazonaws.com/uploads/raas/Partners/YAKOPI/Aceh+Timur/Stories/YAK-worker_holds_propagules_Aceh-0624(3)+(1).avif'
    },
    {
      id: 7,
      title: 'Mangrove - How we restore mangroves',
      location: 'Probolingo, Indonesia',
      date: '2021–2024',
      description:
        'Yagasu’s mangrove restoration effort in Probolingo, Indonesia, captures the transformation from a damaged coastline to a thriving ecosystem. In 2021, deforestation and erosion had left the area nearly bare, but local community efforts began to change that. By 2023 and 2024, healthy mangrove forests emerged, with roots stabilizing the soil and protecting the coast from erosion.',
      imgUrl:
        'https://handprint-live.s3.amazonaws.com/uploads/raas/Partners/Yagasu/Situbondo/Stories/YAG-Community.avif'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ pt: 4, pb: 6 }}>
      {/* Header with JSON toggle */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'rgb(79,111,82)' }}>
          Impact Stories
        </Typography>
      </Box>

      {/* Subtitle emphasizing engagement and emotional connection */}
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
        Engage customers with real-time impact stories that foster emotional connections with our API
      </Typography>

      {/* Stories feed using flexbox */}

      <ToggleCard title="&nbsp;" code={{ stories }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: isDesktop ? 4 : 2,
          justifyContent: 'center'
        }}
      >
        {stories.map((story) => (
          <Box
            key={story.id}
            sx={{
              width: { xs: '100%', sm: '48%', md: '31%' }
            }}
          >
            <Card sx={{ boxShadow: 2 }}>
              <CardMedia
                component="img"
                height="200"
                image={story.imgUrl}
                alt={story.title}
              />
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  {story.title}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  <strong>{story.location} — {story.date}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {story.description}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      </ToggleCard>
    </Container>
  );
};

export default Engagement;
